import {
  formatOrderDisplayCodeLabel,
  formatOrderItemOptionsLabel,
  type CustomerOrderDetail,
  type CustomerOrderItemRow,
} from '../../domain/orderTypes';
import { isMercadoPagoPaidOrder, isPayAtPickupOrder } from '../../domain/orderPayment';
import { formatArPhoneDisplay } from '@shared/phone';
import { formatQtyCompact } from '@shared/quantity';
import { formatMoney } from '../../../shared/format/public-api';

export type OrderComandaPayload = { text: string; html: string };

function money(n: number): string {
  return `${formatMoney(Math.round(n))}`;
}

function itemName(item: CustomerOrderItemRow): string {
  if (item.item_status === 'replaced' && item.effective_product_name) {
    return item.effective_product_name;
  }
  return item.product_name;
}

function itemLineTotal(item: CustomerOrderItemRow): number {
  if (item.item_status === 'replaced' && item.effective_line_total != null) {
    return item.effective_line_total;
  }
  return item.line_total;
}

function channelLine(order: CustomerOrderDetail): string {
  const channel = order.pickup_type === 'delivery' ? 'DELIVERY' : 'RETIRO';
  if (order.pickup_type !== 'delivery') return channel;
  const fulfillment =
    order.delivery_fulfillment === 'platform_rider'
      ? 'Vuelto'
      : order.delivery_fulfillment === 'merchant'
        ? 'Propio'
        : null;
  return fulfillment ? `${channel} · ${fulfillment}` : channel;
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}

function paymentLine(order: CustomerOrderDetail): string {
  if (isMercadoPagoPaidOrder(order) || order.payment_status === 'paid') return 'PAGO: PAGADO';
  if (isPayAtPickupOrder(order)) return 'PAGO: COBRAR EN CAJA';
  return `PAGO: ${(order.payment_status ?? 'pendiente').toUpperCase()}`;
}

function activeItems(order: CustomerOrderDetail): CustomerOrderItemRow[] {
  return order.items.filter((i) => i.item_status !== 'removed');
}

/** Builder único para Imprimir / Compartir comanda (58–80 mm). Nunca incluye PIN. */
export function buildOrderComanda(order: CustomerOrderDetail): OrderComandaPayload {
  const code = formatOrderDisplayCodeLabel(order.display_code, order.id);
  const items = activeItems(order);
  const sep = '----------------';

  const textLines: (string | null)[] = [
    `    ${code}`,
    channelLine(order),
    whenLabel(order.created_at),
    sep,
    order.customer_name,
    order.customer_phone ? `Tel: ${formatArPhoneDisplay(order.customer_phone)}` : null,
    order.pickup_type === 'delivery' && order.delivery_address
      ? `Dir: ${order.delivery_address}`
      : null,
    order.pickup_type === 'pickup' && order.pickup_time_slot
      ? `Slot: ${order.pickup_time_slot}`
      : null,
    order.notes ? `Notas: ${order.notes}` : null,
    order.contains_restricted ? '+18 · Verificar DNI' : null,
    paymentLine(order),
    order.pickup_verification_required ? 'El repartidor confirma el código' : null,
    sep,
    ...items.flatMap((i) => {
      const left = `${formatQtyCompact(i.quantity, i.unit_of_measure)}× ${itemName(i)}`;
      const right = money(itemLineTotal(i));
      const pad = Math.max(1, 28 - left.length - right.length);
      const opts = formatOrderItemOptionsLabel(i.options);
      const note = i.line_note?.trim() || '';
      return [
        `${left}${' '.repeat(pad)}${right}`,
        opts ? `  ${opts}` : null,
        note ? `  Nota: ${note}` : null,
      ].filter((l): l is string => l != null);
    }),
    ...order.discounts.map((d) => `− ${money(d.discount_amount)} · ${d.label}`),
    order.delivery_fee != null && order.delivery_fee > 0
      ? `Envío ${money(order.delivery_fee)}`
      : null,
    sep,
    `Total ${money(order.total_amount)}`,
    'Pegá o meté este ticket en la bolsa',
  ];

  const text = textLines.filter((l): l is string => l != null && l !== '').join('\n');

  const itemRowsHtml = items
    .map((i) => {
      const name = escapeHtml(itemName(i));
      const opts = formatOrderItemOptionsLabel(i.options);
      const note = i.line_note?.trim() || '';
      const meta = [
        opts ? `<div class="mod">${escapeHtml(opts)}</div>` : '',
        note ? `<div class="mod">Nota: ${escapeHtml(note)}</div>` : '',
      ].join('');
      return `<tr><td class="qty">${escapeHtml(formatQtyCompact(i.quantity, i.unit_of_measure))}×</td><td class="name">${name}${meta}</td><td class="price">${money(itemLineTotal(i))}</td></tr>`;
    })
    .join('');

  const discountsHtml = order.discounts
    .map(
      (d) =>
        `<div class="disc">− ${money(d.discount_amount)} · ${escapeHtml(d.label)}</div>`,
    )
    .join('');

  const alertsHtml = [
    order.contains_restricted
      ? '<div class="alert">+18 · Verificar DNI</div>'
      : '',
    `<div class="alert">${escapeHtml(paymentLine(order))}</div>`,
    order.pickup_verification_required
      ? '<div class="alert">El repartidor confirma el código</div>'
      : '',
  ].join('');

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/>
<style>
  @page { margin: 4mm; size: 72mm auto; }
  @media print { body { margin: 0; } }
  body {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px; color: #111; margin: 0; padding: 6px;
    max-width: 72mm; width: 72mm; box-sizing: border-box;
  }
  .code {
    text-align: center; font-size: 56px; font-weight: 800;
    letter-spacing: 0.18em; line-height: 1.1; margin: 4px 0 8px;
  }
  .center { text-align: center; }
  .meta { margin: 2px 0; }
  .sep { border: none; border-top: 1px solid #111; margin: 8px 0; }
  .notes { font-weight: 700; margin: 4px 0; }
  .alert {
    font-weight: 800; border: 1px solid #111; padding: 4px 6px; margin: 4px 0;
  }
  table { width: 100%; border-collapse: collapse; }
  td { vertical-align: top; padding: 2px 0; }
  td.qty { width: 2.2em; white-space: nowrap; }
  td.name { word-break: break-word; }
  td.name .mod { font-size: 11px; margin-top: 1px; }
  td.price { text-align: right; white-space: nowrap; padding-left: 6px; }
  .disc { margin: 2px 0; }
  .total { font-weight: 800; font-size: 14px; margin-top: 4px; }
  .foot { text-align: center; font-size: 10px; margin-top: 10px; }
</style></head><body>
  <div class="code">${escapeHtml(code)}</div>
  <div class="center meta">${escapeHtml(channelLine(order))}</div>
  <div class="center meta">${escapeHtml(whenLabel(order.created_at))}</div>
  <hr class="sep"/>
  <div class="meta"><strong>${escapeHtml(order.customer_name)}</strong></div>
  ${order.customer_phone ? `<div class="meta">Tel: ${escapeHtml(formatArPhoneDisplay(order.customer_phone))}</div>` : ''}
  ${
    order.pickup_type === 'delivery' && order.delivery_address
      ? `<div class="meta">Dir: ${escapeHtml(order.delivery_address)}</div>`
      : ''
  }
  ${
    order.pickup_type === 'pickup' && order.pickup_time_slot
      ? `<div class="meta">Slot: ${escapeHtml(order.pickup_time_slot)}</div>`
      : ''
  }
  ${order.notes ? `<div class="notes">Notas: ${escapeHtml(order.notes)}</div>` : ''}
  ${alertsHtml}
  <hr class="sep"/>
  <table>${itemRowsHtml}</table>
  ${discountsHtml}
  ${
    order.delivery_fee != null && order.delivery_fee > 0
      ? `<div class="meta">Envío ${money(order.delivery_fee)}</div>`
      : ''
  }
  <hr class="sep"/>
  <div class="total">Total ${money(order.total_amount)}</div>
  <div class="foot">Pegá o meté este ticket en la bolsa</div>
</body></html>`;

  return { text, html };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
